<script setup lang="ts">
import { computed } from "vue";
import { useHead } from "@unhead/vue";
import { useI18n } from "vue-i18n";

const { t, locale } = useI18n();

useHead(
  computed(() => ({
    title: t("privacyPolicy.meta.title"),
    meta: [
      { name: "description", content: t("privacyPolicy.meta.description") },
      { name: "robots", content: "index,follow" },
    ],
    htmlAttrs: { lang: locale.value },
  })),
);
</script>

<template>
  <article class="mx-auto max-w-[720px] px-5 py-12 md:px-8 md:py-20">
    <router-link
      to="/"
      class="inline-flex items-center gap-1 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]"
    >
      <UIcon name="i-lucide-arrow-left" class="size-4" />
      {{ t("privacyPolicy.backToHome") }}
    </router-link>

    <h1
      class="mt-6 text-3xl font-semibold text-[var(--color-text-primary)] md:text-4xl"
      style="font-family: var(--font-display)"
    >
      {{ t("privacyPolicy.heading") }}
    </h1>
    <p class="mt-2 text-sm text-[var(--color-text-muted)]">
      {{ t("privacyPolicy.lastUpdated") }}
    </p>

    <div class="mt-10 space-y-10 text-[15px] leading-relaxed text-[var(--color-text-secondary)]">
      <section>
        <h2 class="text-xl font-semibold text-[var(--color-text-primary)]">
          {{ t("privacyPolicy.section1.heading") }}
        </h2>
        <p class="mt-3">{{ t("privacyPolicy.section1.p1") }}</p>
        <p class="mt-3">
          <strong>HeartQuake Sagl</strong><br />
          Via Rivascia 11<br />
          6518 Gorduno, Switzerland<br />
          {{ t("privacyPolicy.section1.addressContactLabel") }}
          <a href="mailto:privacy@cresci.kids" class="underline">privacy@cresci.kids</a>
        </p>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-[var(--color-text-primary)]">
          {{ t("privacyPolicy.section2.heading") }}
        </h2>
        <p class="mt-3">{{ t("privacyPolicy.section2.intro") }}</p>
        <h3 class="mt-6 text-base font-semibold text-[var(--color-text-primary)]">
          {{ t("privacyPolicy.section2.browseHeading") }}
        </h3>
        <ul class="mt-3 list-disc space-y-2 pl-6">
          <li>{{ t("privacyPolicy.section2.browseItem1") }}</li>
          <li>{{ t("privacyPolicy.section2.browseItem2") }}</li>
        </ul>
        <h3 class="mt-6 text-base font-semibold text-[var(--color-text-primary)]">
          {{ t("privacyPolicy.section2.orderHeading") }}
        </h3>
        <ul class="mt-3 list-disc space-y-2 pl-6">
          <li>
            <strong>{{ t("privacyPolicy.section2.orderItem1Label") }}</strong>
            {{ t("privacyPolicy.section2.orderItem1Body") }}
          </li>
          <li>
            <strong>{{ t("privacyPolicy.section2.orderItem2Label") }}</strong>
            {{ t("privacyPolicy.section2.orderItem2Body") }}
          </li>
          <li>
            <strong>{{ t("privacyPolicy.section2.orderItem3Label") }}</strong>
            {{ t("privacyPolicy.section2.orderItem3Body") }}
          </li>
          <li>
            <strong>{{ t("privacyPolicy.section2.orderItem4Label") }}</strong>
            {{ t("privacyPolicy.section2.orderItem4Body") }}
          </li>
        </ul>
        <h3 class="mt-6 text-base font-semibold text-[var(--color-text-primary)]">
          {{ t("privacyPolicy.section2.emailHeading") }}
        </h3>
        <p class="mt-3">{{ t("privacyPolicy.section2.emailBody") }}</p>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-[var(--color-text-primary)]">
          {{ t("privacyPolicy.section3.heading") }}
        </h2>
        <ul class="mt-3 list-disc space-y-2 pl-6">
          <li>
            <strong>{{ t("privacyPolicy.section3.item1Label") }}</strong>
            {{ t("privacyPolicy.section3.item1Body") }}
          </li>
          <li>
            <strong>{{ t("privacyPolicy.section3.item2Label") }}</strong>
            {{ t("privacyPolicy.section3.item2Body") }}
          </li>
          <li>
            <strong>{{ t("privacyPolicy.section3.item3Label") }}</strong>
            {{ t("privacyPolicy.section3.item3Body") }}
          </li>
          <li>
            <strong>{{ t("privacyPolicy.section3.item4Label") }}</strong>
            {{ t("privacyPolicy.section3.item4Body") }}
          </li>
        </ul>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-[var(--color-text-primary)]">
          {{ t("privacyPolicy.section4.heading") }}
        </h2>
        <ul class="mt-3 list-disc space-y-2 pl-6">
          <li>
            <strong>{{ t("privacyPolicy.section4.item1Label") }}</strong>
            {{ t("privacyPolicy.section4.item1Body") }}
          </li>
          <li>
            <strong>{{ t("privacyPolicy.section4.item2Label") }}</strong>
            {{ t("privacyPolicy.section4.item2Body") }}
          </li>
          <li>
            <strong>{{ t("privacyPolicy.section4.item3Label") }}</strong>
            {{ t("privacyPolicy.section4.item3Body") }}
          </li>
          <li>{{ t("privacyPolicy.section4.item4") }}</li>
        </ul>
        <p class="mt-3">{{ t("privacyPolicy.section4.blogDisclosure") }}</p>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-[var(--color-text-primary)]">
          {{ t("privacyPolicy.section5.heading") }}
        </h2>
        <p class="mt-3">{{ t("privacyPolicy.section5.p1") }}</p>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-[var(--color-text-primary)]">
          {{ t("privacyPolicy.section6.heading") }}
        </h2>
        <ul class="mt-3 list-disc space-y-2 pl-6">
          <li>{{ t("privacyPolicy.section6.item1") }}</li>
          <li>{{ t("privacyPolicy.section6.item2") }}</li>
          <li>{{ t("privacyPolicy.section6.item3") }}</li>
        </ul>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-[var(--color-text-primary)]">
          {{ t("privacyPolicy.section7.heading") }}
        </h2>
        <p class="mt-3">{{ t("privacyPolicy.section7.intro") }}</p>
        <ul class="mt-3 list-disc space-y-2 pl-6">
          <li>{{ t("privacyPolicy.section7.item1") }}</li>
          <li>{{ t("privacyPolicy.section7.item2") }}</li>
          <li>{{ t("privacyPolicy.section7.item3") }}</li>
          <li>{{ t("privacyPolicy.section7.item4") }}</li>
          <li>{{ t("privacyPolicy.section7.item5") }}</li>
          <li>{{ t("privacyPolicy.section7.item6") }}</li>
          <li>{{ t("privacyPolicy.section7.item7") }}</li>
        </ul>
        <p class="mt-3">
          {{ t("privacyPolicy.section7.exerciseBefore") }}
          <a href="mailto:privacy@cresci.kids" class="underline">privacy@cresci.kids</a
          >{{ t("privacyPolicy.section7.exerciseAfter") }}
        </p>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-[var(--color-text-primary)]">
          {{ t("privacyPolicy.section8.heading") }}
        </h2>
        <p class="mt-3">{{ t("privacyPolicy.section8.p1") }}</p>
      </section>

      <section>
        <h2 class="text-xl font-semibold text-[var(--color-text-primary)]">
          {{ t("privacyPolicy.section9.heading") }}
        </h2>
        <p class="mt-3">{{ t("privacyPolicy.section9.p1") }}</p>
      </section>
    </div>
  </article>
</template>
